import { useMemo, useState } from "react";
import { Button, Switch, Table, Tag, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import TableToolbar from "@/components/UI/TableToolbar/TableToolbar";
import FetchWrapper from "@/components/UI/FetchWraper";
import PageHeader from "@/components/UI/PageHeader";
import { BASE_ENDPOINT } from "@/components/Templates/constants";
import { useScopeParams } from "@/hooks/useScopeParams";
import ruleStudioApi from "@/services/ruleStudioApi";
import RuleWizard from "./RuleWizard";
import RuleDetailDrawer from "./RuleDetailDrawer";
import {
  CORE_RULE_ID_BASE,
  type RuleDefinition,
  type RuleKind,
  type SystemRule,
} from "./ruleStudio.types";

const REVIEW_KINDS: RuleKind[] = ["review_materiality", "review_sla"];

const SEVERITY_DOT: Record<string, string> = {
  Critical: "#B3372E",
  Major: "#D97B29",
  Minor: "#8FA5A0",
};

const STATUS_COLORS: Record<string, string> = {
  active: "green",
  draft: "default",
  paused: "orange",
};

/** One row of the unified rules table (system rules + custom definitions). */
type RuleRow = {
  key: string;
  definitionId: number | null;
  name: string;
  level: string;
  domain: string;
  severity: string | null;
  origin: "System" | "Custom";
  kind: RuleKind;
  status: string;
  lastRun: string | null;
  openCount: number | null;
  enabled: boolean | null;
  description: string;
};

const Rules = () => {
  return (
    <FetchWrapper endpointSuffix="issue_rules/">
      {(data) => <RulesScreen systemRules={data} />}
    </FetchWrapper>
  );
};

const RulesScreen = ({ systemRules }: { systemRules: SystemRule[] }) => {
  const { id } = useScopeParams();
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [openRuleId, setOpenRuleId] = useState<number | null>(null);

  const customQueryKey = ["rule-studio-rules", id];
  const { data: customRules = [] } = useQuery({
    queryKey: customQueryKey,
    queryFn: () => ruleStudioApi.listRules(id),
  });

  const refetchCustom = () =>
    queryClient.invalidateQueries({ queryKey: customQueryKey });

  const toggleMutation = useMutation({
    mutationFn: ({ ruleId, enabled }: { ruleId: number; enabled: boolean }) =>
      ruleStudioApi.toggleRule(id, ruleId, enabled),
    onSuccess: refetchCustom,
  });

  const rows: RuleRow[] = useMemo(() => {
    const customRows = customRules.map(
      (rule: RuleDefinition): RuleRow => ({
        key: `custom-${rule.id}`,
        definitionId: rule.id,
        name: rule.name,
        level: rule.level,
        domain: rule.domain ?? "-",
        severity: rule.severity,
        origin: "Custom",
        kind: rule.rule_kind ?? "discrepancy",
        status: rule.status,
        lastRun: rule.last_run?.started_at ?? null,
        openCount: rule.open_count,
        enabled: rule.enabled_for_trial,
        description: rule.description ?? "",
      }),
    );
    // Definition mirrors live in the same core table as ingested rules; the
    // custom rows above already represent them.
    const systemRows = (systemRules ?? [])
      .filter((rule) => rule.discrepancy_rule_id < CORE_RULE_ID_BASE)
      .map(
        (rule): RuleRow => ({
          key: `system-${rule.discrepancy_rule_id}`,
          definitionId: null,
          name:
            rule.discrepancy_rule_category ||
            rule.discrepancy_rule_description ||
            `Rule ${rule.discrepancy_rule_id}`,
          level: rule.discrepancy_rule_level ?? "-",
          domain: rule.discrepancy_rule_data_source ?? "-",
          severity: null,
          origin: "System",
          kind: "discrepancy",
          status: "active",
          lastRun: null,
          openCount: null,
          enabled: null,
          description:
            rule.discrepancy_rule_description ??
            rule.discrepancy_rule_details ??
            "",
        }),
      );
    return [...customRows, ...systemRows];
  }, [customRules, systemRules]);

  // Medical review rules configure how review classifies change; they
  // materialise no discrepancies and belong to Safety, not to data management.
  // They are edited on Medical Review's Watch list, so they are not listed
  // here at all — a tab for them inside this screen put one discipline's
  // policy inside another's workspace.
  const visibleRows = useMemo(
    () => rows.filter((row) => !REVIEW_KINDS.includes(row.kind)),
    [rows],
  );

  const columns = [
    {
      title: "Rule",
      dataIndex: "name",
      key: "name",
      width: 340,
      sorter: (a: RuleRow, b: RuleRow) => a.name.localeCompare(b.name),
      render: (text: string, record: RuleRow) =>
        record.definitionId !== null ? (
          <button
            type="button"
            className="text-left text-[#08709B] hover:underline"
            onClick={() => setOpenRuleId(record.definitionId)}
          >
            {text}
          </button>
        ) : (
          <Typography.Paragraph
            className="!mb-0"
            ellipsis={{ tooltip: true, rows: 2 }}
          >
            {text}
          </Typography.Paragraph>
        ),
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      width: 110,
      sorter: (a: RuleRow, b: RuleRow) => a.level.localeCompare(b.level),
    },
    {
      title: "Domain",
      dataIndex: "domain",
      key: "domain",
      width: 140,
      sorter: (a: RuleRow, b: RuleRow) => a.domain.localeCompare(b.domain),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      width: 110,
      render: (severity: string | null) =>
        severity ? (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="w-[7px] h-[7px] rounded-[2px] inline-block"
              style={{ background: SEVERITY_DOT[severity] ?? "#8FA5A0" }}
            />
            {severity}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Origin",
      dataIndex: "origin",
      key: "origin",
      width: 100,
      filters: [
        { text: "System", value: "System" },
        { text: "Custom", value: "Custom" },
      ],
      onFilter: (value: unknown, record: RuleRow) => record.origin === value,
      render: (origin: string) => (
        <Tag color={origin === "Custom" ? "blue" : "default"}>{origin}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] ?? "default"}>{status}</Tag>
      ),
    },
    {
      title: "Last run",
      dataIndex: "lastRun",
      key: "lastRun",
      width: 160,
      render: (value: string | null, record: RuleRow) =>
        record.origin === "System"
          ? "with data refresh"
          : value
            ? new Date(value).toLocaleString()
            : "—",
    },
    {
      title: "Open",
      dataIndex: "openCount",
      key: "openCount",
      width: 80,
      align: "right" as const,
      sorter: (a: RuleRow, b: RuleRow) =>
        (a.openCount ?? -1) - (b.openCount ?? -1),
      render: (value: number | null) => (value === null ? "—" : value),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string) => (
        <Typography.Paragraph
          className="!mb-0"
          ellipsis={{ tooltip: true, rows: 2 }}
        >
          {text || "-"}
        </Typography.Paragraph>
      ),
    },
    {
      title: "On",
      dataIndex: "enabled",
      key: "enabled",
      width: 70,
      render: (enabled: boolean | null, record: RuleRow) =>
        record.definitionId === null ? null : (
          <Switch
            size="small"
            checked={Boolean(enabled)}
            loading={
              toggleMutation.isPending &&
              toggleMutation.variables?.ruleId === record.definitionId
            }
            onChange={(checked) =>
              toggleMutation.mutate({
                ruleId: record.definitionId as number,
                enabled: checked,
              })
            }
          />
        ),
    },
  ];

  return (
    <div className="w-full">
      <div className="flex justify-between items-center">
        <PageHeader
          title="Rules"
          tooltipText="Deterministic checks that run against this trial's data after every refresh. Matches are recorded as discrepancies."
        />
        <div className="flex items-center gap-2">
          <TableToolbar
            name="rules"
            datasetName="issues_rules"
            guideLink="/wiki/guides/data-discrepancies"
            endpointSuffix={BASE_ENDPOINT}
            noFilteredDownload={true}
          />
          <Button type="primary" onClick={() => setWizardOpen(true)}>
            + New rule
          </Button>
        </div>
      </div>
      <Table
        columns={columns as never}
        size="small"
        rowKey="key"
        dataSource={visibleRows}
        scroll={{ x: 1400, y: "calc(100vh - 340px)" }}
        pagination={false}
      />
      <RuleWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSaved={() => refetchCustom()}
      />
      <RuleDetailDrawer
        ruleId={openRuleId}
        onClose={() => setOpenRuleId(null)}
        onChanged={refetchCustom}
      />
    </div>
  );
};

export default Rules;
